import { useMemo } from 'react'

import { useCases } from '@/features/cases/api/useCase'
import { useQuotes } from '@/features/quotes/api/useQuote'
import { useClients } from '@/features/directories/api/useDirectory'
import { findCountry } from '@/lib/countries'
import type { CaseStatus, QuoteStatus } from '@/types/domain'

/* Global search over the three record kinds the topbar names: works, quotes,
 * clients (FR-39, UX-DR-23).
 *
 * Matching happens on the CLIENT, over the same three list queries the Works,
 * Quotes and Directory pages already hold. That is a deliberate choice and not
 * a placeholder for a server endpoint:
 *
 *   · The lists are already in the TanStack cache by the time anybody reaches
 *     for the box — the sidebar counts and every list page put them there — so
 *     the common case costs no request at all and answers within a frame.
 *   · There is no /search route on the server, and each list is already scoped
 *     to what the caller may see. Matching over exactly those rows inherits
 *     that scoping for free; a new endpoint would have to re-derive it, and a
 *     search that leaks one agent's name in a result row undoes FR-29 (§3) in
 *     the one place every role looks.
 *
 * The bound that makes this safe is volume: these are hundreds of rows, not
 * hundreds of thousands. When a list outgrows a single response the endpoints
 * gain paging, and this hook is where a server-side query replaces the local
 * filter — the shape it returns is what the popover renders, so that swap does
 * not reach the UI.
 */

export type SearchResultKind = 'case' | 'quote' | 'client'

export interface SearchResult {
  /** Unique across kinds — a case and a quote may share an id namespace. */
  key: string
  kind: SearchResultKind
  id: string
  /** The record's own name: its number, or a client's name. */
  title: string
  /** The one line that tells two same-numbered records apart. */
  subtitle?: string
  status?: CaseStatus | QuoteStatus
  /** Where selecting the row goes. */
  to: string
}

/** How many rows the popover will show. Enough to contain the answer, few
 *  enough that the list stays scannable without scrolling — past ten, a
 *  narrower query is faster than reading further. */
export const SEARCH_RESULT_LIMIT = 10

/** Below two characters every record matches, and the popover becomes noise. */
export const SEARCH_MIN_LENGTH = 2

/** Case- and diacritic-insensitive, and tolerant of the separators people type
 *  inside record numbers: `AB 27`, `ab-27` and `AB27` are one query. Runs on
 *  the query and on each field alike, so both sides are normalised the same. */
function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    // Strip combining marks so `Ё` matches `Е` — Russian client names are
    // entered both ways and nobody should have to guess which.
    .replace(/[̀-ͯ]/g, '')
    .replace(/[\s\-_/.]+/g, '')
}

/** Scores a candidate against the normalised query; null when it does not
 *  match at all.
 *
 *  A prefix beats a substring, and an earlier field beats a later one: typing
 *  `AB` should put case AB-27 above a client whose address contains "ab". */
function score(fields: Array<string | undefined>, query: string): number | null {
  let best: number | null = null

  fields.forEach((field, index) => {
    if (!field) return
    const at = normalize(field).indexOf(query)
    if (at < 0) return

    // Field position dominates, then prefix-vs-substring, then how deep the
    // hit sits in the field.
    const value = index * 100 + (at === 0 ? 0 : 10) + Math.min(at, 9)
    if (best === null || value < best) best = value
  })

  return best
}

/** A routing endpoint as it can be TYPED.
 *
 *  Origin and destination hold an ISO 3166-1 code on anything entered through
 *  the pickers, and search over the raw column would then answer nothing to
 *  "Kazakhstan" — the word the screen shows and the only one a user would
 *  think to type. Both names go in, in BOTH languages, for the same reason the
 *  picker's own filter matches both: the interface language is not a promise
 *  about which language a query arrives in. A value that is not a code — a city
 *  from before the pickers — is searched as written. */
function placeTerms(value: string | undefined): Array<string | undefined> {
  const country = findCountry(value)
  return country ? [country.code, country.name, country.nameRu] : [value]
}

export interface GlobalSearch {
  results: SearchResult[]
  /** True while any of the three lists is still arriving AND nothing has
   *  matched yet — so a cached list renders instantly and only a genuinely
   *  empty screen shows a loading state. */
  isLoading: boolean
  /** True when the query is too short to run. */
  isIdle: boolean
}

/**
 * Matches `query` across works, quotes and clients.
 *
 * The three lists are fetched only while the search box is open (`enabled`),
 * so the topbar does not pull the whole directory on every page load for a
 * feature most sessions never touch. Once fetched they are the same cache
 * entries the list pages use, so opening search after visiting Works costs
 * nothing.
 */
export function useGlobalSearch(query: string, enabled: boolean): GlobalSearch {
  const trimmed = query.trim()
  const isIdle = trimmed.length < SEARCH_MIN_LENGTH
  const active = enabled && !isIdle

  const cases = useCases({ enabled: active })
  const quotes = useQuotes({ enabled: active })
  const clients = useClients(undefined, { enabled: active })

  const results = useMemo(() => {
    if (isIdle) return []

    const needle = normalize(trimmed)
    if (!needle) return []

    const scored: Array<{ result: SearchResult; rank: number }> = []

    /* Field order inside each record is match PRIORITY, not display order: the
       number and the client name are what people search by, the lane is what
       they fall back to. */
    for (const record of cases.data ?? []) {
      const rank = score(
        [
          record.number,
          record.clientName,
          ...placeTerms(record.origin),
          ...placeTerms(record.destination),
        ],
        needle,
      )
      if (rank === null) continue
      scored.push({
        rank,
        result: {
          key: `case:${record.id}`,
          kind: 'case',
          id: record.id,
          title: record.number,
          subtitle: record.clientName,
          status: record.status,
          to: `/cases/${record.id}`,
        },
      })
    }

    for (const record of quotes.data ?? []) {
      const rank = score(
        [
          record.number,
          record.clientName,
          ...placeTerms(record.origin),
          ...placeTerms(record.destination),
        ],
        needle,
      )
      if (rank === null) continue
      scored.push({
        rank,
        result: {
          key: `quote:${record.id}`,
          kind: 'quote',
          id: record.id,
          title: record.number,
          subtitle: record.clientName,
          status: record.status,
          to: `/quotes/${record.id}`,
        },
      })
    }

    for (const record of clients.data ?? []) {
      const rank = score([record.name, record.code, record.taxId], needle)
      if (rank === null) continue
      scored.push({
        rank,
        result: {
          key: `client:${record.id}`,
          kind: 'client',
          id: record.id,
          title: record.name,
          subtitle: record.code,
          to: `/clients/${encodeURIComponent(record.id)}`,
        },
      })
    }

    /* Kind order breaks a rank tie so the list never reshuffles between two
       renders that scored the same — works first, because they are the record
       people chase mid-shipment. */
    const KIND_ORDER: Record<SearchResultKind, number> = {
      case: 0,
      quote: 1,
      client: 2,
    }

    return scored
      .sort(
        (a, b) =>
          a.rank - b.rank ||
          KIND_ORDER[a.result.kind] - KIND_ORDER[b.result.kind] ||
          a.result.title.localeCompare(b.result.title),
      )
      .slice(0, SEARCH_RESULT_LIMIT)
      .map((entry) => entry.result)
  }, [isIdle, trimmed, cases.data, quotes.data, clients.data])

  return {
    results,
    isLoading:
      active &&
      results.length === 0 &&
      (cases.isLoading || quotes.isLoading || clients.isLoading),
    isIdle,
  }
}
